import { useState } from "react";
import { C } from "../../lib/colors.js";
import { fmtResolveDate } from "../../lib/formatDates.js";
import { answerSummary, shortDate } from "../../lib/forecast/answerSummary.js";
import { rootEngine, tierShortLabel } from "../../lib/forecast/engines.js";
import { fmtChance, fmtShift } from "../../lib/timeline/signposts.js";
import {
  bottomLineRows,
  bottomLineSourceOf,
  phoneSignposts,
  questionOf,
  updatedAtOf,
} from "../../lib/phone/phoneModel.js";
import { useBriefBottomLine } from "../../hooks/useBriefBottomLine.js";
import ChangeText from "../ChangeText.jsx";
import { Button, Chip, Skeleton, Spinner } from "../ui/Primitives.jsx";
import { IconArrowUp, IconCheck, IconChevronDown, IconReport } from "../ui/Icons.jsx";
import { TriggerMark } from "../TriggerNode.jsx";

// How many outcomes and triggers show before "Show all".
const OUTCOMES_SHOWN = 6;
const TRIGGERS_SHOWN = 4;

function StatusChip({ status }) {
  if (!status) return null;
  const tone =
    status.kind === "resolved"
      ? C.gold
      : status.kind === "error"
        ? C.danger
        : status.kind === "running"
          ? C.accent
          : C.grayL;
  return (
    <Chip style={{ color: tone, borderColor: `color-mix(in srgb, ${tone} 40%, transparent)` }}>
      {status.kind === "running" ? <Spinner /> : status.kind === "resolved" ? <IconCheck /> : null}
      {status.label}
    </Chip>
  );
}

/** "−1 pt from the previous forecast (Oct 14)", coloured by direction (raises / lowers). */
function ChangeLine({ change }) {
  if (!change) return null;
  const tone =
    change.direction === "up" ? C.blueTeam : change.direction === "down" ? C.redTeam : C.gray;
  return (
    <span className="sy-ph__change" style={{ color: tone }}>
      <ChangeText text={change.text} />
      <span style={{ color: C.gray }}>{change.since}</span>
    </span>
  );
}

/** The headline number: "21" with a smaller "%". */
function HeroNumber({ text }) {
  const m = /^(.*?)(%)$/.exec(text || "");
  return (
    <div className="sy-ph__heronum sy-num">
      {m ? m[1] : text}
      {m && <small>%</small>}
    </div>
  );
}

/** Every outcome as a labelled bar on one 0–100% scale: named by probability, Other last. */
function Outcomes({ answer }) {
  const [all, setAll] = useState(false);
  if (answer.kind === "binary") {
    const [yes, no] = answer.outcomes;
    // The larger side's figure is the bright one, as on the header.
    const lit = (o) => (o.isLeader ? { color: C.white } : undefined);
    return (
      <div className="sy-ph__dist" aria-label={`Yes ${yes.pText}, No ${no.pText}`}>
        <div>
          <div className="sy-ph__split" role="img" aria-hidden="true">
            <i style={{ flex: `${yes.p} 1 0px`, background: C.accent }} />
            <i
              style={{
                flex: `${no.p} 1 0px`,
                background: `color-mix(in srgb, ${C.grayL} 22%, transparent)`,
              }}
            />
          </div>
          <div className="sy-ph__splitlegend">
            <span>
              Yes{" "}
              <span className="sy-num" style={lit(yes)}>
                {yes.pText}
              </span>
            </span>
            <span>
              No{" "}
              <span className="sy-num" style={lit(no)}>
                {no.pText}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }
  const named = answer.outcomes.filter((o) => !o.isOther);
  const other = answer.outcomes.filter((o) => o.isOther);
  const hidden = Math.max(0, named.length - OUTCOMES_SHOWN);
  const rows = [...(all ? named : named.slice(0, OUTCOMES_SHOWN)), ...other];
  return (
    <div className="sy-ph__dist">
      {rows.map((o, i) => (
        <div
          key={`${o.label}-${i}`}
          className="sy-ph__drow"
          data-leader={o.isLeader && !o.isOther ? "" : undefined}
          data-other={o.isOther ? "" : undefined}
        >
          <span className="sy-ph__dlabel">{o.label}</span>
          <span className="sy-ph__dpct sy-num">{o.pText}</span>
          <span className="sy-ph__dbar" aria-hidden="true">
            <i style={{ width: `${Math.max(0, Math.min(1, o.p)) * 100}%` }} />
          </span>
        </div>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="sy-ph__more"
          data-ui="phone.outcomes-toggle"
          aria-expanded={all}
          onClick={() => setAll((v) => !v)}
        >
          {all ? "Show fewer" : `Show all ${named.length + other.length} outcomes`}
          <IconChevronDown style={{ transform: all ? "rotate(180deg)" : "none" }} />
        </button>
      )}
    </div>
  );
}

/** "The bottom line": Why / The risk / Watch, from the brief's own lines when written. */
function BottomLine({ forecast, nowMs, concluded }) {
  const read = bottomLineSourceOf(forecast, new Date(nowMs));
  // A settled question has nothing left to watch.
  const source = read && concluded ? { ...read, watch: "" } : read;
  const { lines, pending } = useBriefBottomLine({
    enabled: !!source,
    question: source?.question ?? "",
    narrative: source?.narrative ?? "",
    uncertainties: source?.uncertainties ?? [],
  });
  if (!source) return null;
  const rows = bottomLineRows(source, lines);
  if (!rows.length && !pending) return null;
  return (
    <section className="sy-ph__section" data-surface="phone.bottom-line">
      <h2 className="sy-ph__h2">The bottom line</h2>
      <div className="sy-ph__card sy-ph__bl">
        {pending ? (
          <>
            {["Why", "The risk"].map((label) => (
              <div key={label}>
                <div className="sy-ph__bllabel">{label}</div>
                <Skeleton width="96%" height={13} style={{ marginTop: 8 }} />
                <Skeleton width="88%" height={13} style={{ marginTop: 8 }} />
                <Skeleton width="54%" height={13} style={{ marginTop: 8 }} />
              </div>
            ))}
            {rows
              .filter((r) => r.label === "Watch")
              .map((r) => (
                <div key={r.label}>
                  <div className="sy-ph__bllabel">{r.label}</div>
                  <p className="sy-ph__bltext">{r.text}</p>
                </div>
              ))}
          </>
        ) : (
          rows.map((r) => (
            <div key={r.label}>
              <div className="sy-ph__bllabel">{r.label}</div>
              <p className="sy-ph__bltext">{r.text}</p>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/** "4 earlier triggers already happened." / "3 earlier triggers have settled: 2 happened, 1 didn't." */
function settledNote(n, happened) {
  const what = n === 1 ? "1 earlier trigger" : `${n} earlier triggers`;
  const tail = "The full report lists them.";
  if (happened === n) return `${what} already happened. ${tail}`;
  if (happened === 0) return `${what} did not happen. ${tail}`;
  return `${what} have settled: ${happened} happened, ${n - happened} didn't. ${tail}`;
}

/** "What would move it": upcoming triggers as stacked rows, biggest effect first. */
function WhatWouldMoveIt({ forecast, nowMs, concluded, resolutions }) {
  const [all, setAll] = useState(false);
  const { upcoming, settled } = phoneSignposts(forecast, {
    todayMs: nowMs,
    concluded,
    resolutions,
  });
  if (!upcoming.length && !settled.length) return null;
  const rows = all ? upcoming : upcoming.slice(0, TRIGGERS_SHOWN);
  const happened = settled.filter((s) => s.occurred).length;
  return (
    <section className="sy-ph__section" data-surface="phone.signposts">
      <h2 className="sy-ph__h2">What would move it</h2>
      {upcoming.length > 0 ? (
        <>
          <p className="sy-ph__sub">Upcoming triggers, the biggest effect first</p>
          <div className="sy-ph__card">
            {rows.map((row) => {
              const moves =
                row.shift != null && row.shift >= 0.5
                  ? `Could move the forecast ${fmtShift(row.shift)}`
                  : "";
              return (
                <div key={row.key} className="sy-ph__sprow">
                  <span className="sy-ph__spmark">
                    <TriggerMark
                      size={14}
                      markSize={12}
                      chance={row.chance}
                      color={C.grayL}
                      bg={C.panel}
                    />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="sy-ph__sptitle">{row.title}</div>
                    <div className="sy-ph__spsub">{row.subtitle}</div>
                    {moves && <div className="sy-ph__spshift">{moves}</div>}
                  </div>
                  <div className="sy-ph__spchance">
                    <b className="sy-num">{fmtChance(row.chance)}</b>
                    <span>chance</span>
                  </div>
                </div>
              );
            })}
            {upcoming.length > TRIGGERS_SHOWN && (
              <div className="sy-ph__spfoot">
                <button
                  type="button"
                  className="sy-ph__more"
                  data-ui="phone.triggers-toggle"
                  aria-expanded={all}
                  onClick={() => setAll((v) => !v)}
                >
                  {all ? "Show fewer" : `Show all ${upcoming.length} triggers`}
                  <IconChevronDown style={{ transform: all ? "rotate(180deg)" : "none" }} />
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="sy-ph__sub">No triggers are still to come.</p>
      )}
      {settled.length > 0 && <p className="sy-ph__note">{settledNote(settled.length, happened)}</p>}
    </section>
  );
}

/**
 * The read-only forecast screen: the question, the answer, the bottom line, what would move it,
 * and the full report.
 * @param {{forecast: any, previous?: object|null, nowMs: number, updatedAt?: string|null,
 *   concluded: boolean, resolutions?: object|null, reportEnabled: boolean,
 *   onOpenReport: () => void, headingRef?: (el: HTMLElement|null) => void}} props
 *   `previous` is the version to compare with; undefined derives it from `forecast`.
 */
export default function PhoneForecast({
  forecast,
  previous,
  nowMs,
  updatedAt,
  concluded,
  resolutions,
  reportEnabled,
  onOpenReport,
  headingRef,
}) {
  const now = new Date(nowMs);
  const answer = answerSummary(forecast, { now, previous });
  const depth = tierShortLabel(rootEngine(forecast));
  const resolves = forecast.contract?.resolution_date;
  const updated = updatedAt || updatedAtOf(forecast);
  const running = answer.status?.kind === "running";
  const hasAnswer = answer.kind !== "none" && !!answer.headlineText;
  return (
    <article data-surface="phone.forecast" data-surface-key={forecast.id}>
      <h1 className="sy-ph__q" ref={headingRef} data-telemetry-redact="">
        {questionOf(forecast) || "Untitled question"}
      </h1>
      {/* Each item draws its own leading dot; the one that starts a line is clipped. */}
      <div className="sy-ph__meta">
        <div className="sy-ph__metain">
          {resolves && <span>Resolves {fmtResolveDate(String(resolves))}</span>}
          {updated && !running && <span>Updated {shortDate(updated, now)}</span>}
          {depth && <span>Depth: {depth}</span>}
        </div>
        {answer.status && answer.status.kind !== "running" && (
          <div style={{ marginTop: 8 }}>
            <StatusChip status={answer.status} />
          </div>
        )}
      </div>

      {running && !hasAnswer ? (
        <div className="sy-ph__running" role="status">
          <Spinner />
          <span>Sayer is still working on this forecast. It appears here when it finishes.</span>
        </div>
      ) : hasAnswer ? (
        <>
          <div className="sy-ph__hero">
            <HeroNumber text={answer.headlineText} />
            <div className="sy-ph__herotext">
              <span className="sy-ph__statement">
                {answer.kind === "binary" ? answer.headlineLabel : answer.leaderStatement}
              </span>
              <ChangeLine change={answer.change} />
            </div>
          </div>
          <Outcomes answer={answer} />
        </>
      ) : (
        <p className="sy-ph__note">This forecast has no answer to show.</p>
      )}

      {!running && <BottomLine forecast={forecast} nowMs={nowMs} concluded={concluded} />}
      <WhatWouldMoveIt
        forecast={forecast}
        nowMs={nowMs}
        concluded={concluded}
        resolutions={resolutions}
      />

      <Button
        variant="primary"
        size="lg"
        className="sy-ph__cta"
        data-ui="phone.open-report"
        data-ui-key={forecast.id}
        icon={<IconReport />}
        disabled={!reportEnabled}
        onClick={onOpenReport}
      >
        {reportEnabled ? "Open full report" : "Report ready when it finishes"}
      </Button>
    </article>
  );
}
